<script setup>
import { ref, onMounted, computed } from 'vue';

import { useRouter } from 'vue-router';
import { LpMeetErnestCta } from '@thehotelsnetwork/lampo';
import { useFeatureFlags } from '@thehotelsnetwork/vue-feature-flags';

import useSectionState from '@/state/useSectionState';
import useUserState from '@/state/useUserState';
import { track } from '@/utils/analytics';
import logout from '@/utils/logout';
import performAccountNavigation from '@/utils/performAccountNavigation';
import { LOGOUT } from '@/config/analyticsEvents';

// Components
import HelpBtn from '@/components/HelpBtn/HelpBtn.vue';
import OptionsList from '@/components/OptionsList/OptionsList.vue';
import ThnProductNav from '@/components/ThnProductNav/ThnProductNav.vue';

const router = useRouter();
const headerRef = ref(null);

const { setScrollbarOffsetHeight } = useSectionState();
const { userData, isStrictlyDMOUser } = useUserState();
const { flags: featureFlags } = useFeatureFlags();

const defaultAction = (path) => {
  router.push(path);
};

// User Options
const userDefaultOptionsList = [
  {
    label: 'My Properties',
    id: 'properties',
    premium: 0,
    action: () => defaultAction('/properties'),
  },
  {
    label: 'My Account',
    id: 'account',
    premium: 0,
    action: () => {
      track('click - preferences');
      performAccountNavigation('/preferences');
    },
  },
  {
    label: 'Notifications',
    id: 'email-notifications',
    premium: 0,
    action: () => {
      track('click - email_notifications');
      performAccountNavigation('/notifications');
    },
  },
  {
    label: 'Contact THN',
    id: 'contact',
    premium: 0,
    action: () => defaultAction('/contact'),
  },
  {
    label: 'Logout',
    id: 'logout',
    premium: 0,
    action: () => {
      track(LOGOUT, { disableSendData: true });
      logout();
    },
  },
];

if (isStrictlyDMOUser.value) {
  userDefaultOptionsList.splice(0, 1);
  userDefaultOptionsList.splice(1, 1);
}

const userOptionsList = computed(() => userDefaultOptionsList
  .filter((option) => userData.premiumLevel >= option.premium));

onMounted(() => setScrollbarOffsetHeight(headerRef.value.clientHeight));
</script>
<template>
  <header
    ref="headerRef"
    :class="[
      'fixed top-0 left-0 z-30 flex items-center justify-between w-full',
      'px-4 py-3 bg-white border-b border-base-300',
    ]"
  >
    <div class="flex items-center">
      <div class="flex items-center mr-[40px]">
        <ThnProductNav />
        <router-link to="/">
          <span class="ml-3 text-lg font-normal tracking-widest">BenchDirect</span>
        </router-link>
      </div>
      <div class="flex flex-grow">
        <slot name="left-side" />
      </div>
    </div>

    <div class="flex items-center">
      <slot name="right-side" />
      <LpMeetErnestCta
        v-if="featureFlags.ernest"
        :has-ernest="userData.ernest"
        join-url="https://www.mylighthouse.com/platform/ernest"
        :ui="{
          label: 'max-xl:hidden'
        }"
      />
      <HelpBtn />
      <LpDropdown
        x-position="right"
      >
        <template #btn>
          <LpButton
            mode="transparent"
            size="small"
            color="gray"
          >
            <div class="flex flex-row items-center space-x-2 cursor-pointer">
              <LpAvatar
                :first-name="userData.firstName"
                :last-name="userData.lastName"
                class="font-normal"
              />
              <div class="text-base font-normal">
                {{ userData.firstName }}
              </div>
            </div>
          </LpButton>
        </template>
        <template #menu="{ closeMenu }">
          <OptionsList
            :options="userOptionsList"
            class="min-w-options-list"
            @click="closeMenu"
          />
        </template>
      </LpDropdown>
    </div>
  </header>
</template>
